import { Card, CardContent, CardIcon, CardTitle, CardValue, CryptoIcon } from '../ui'
import type { CompanyStats } from '../pages/Company'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../ui'
import { ExternalLink } from 'lucide-react'
import { cn } from '../../lib/utils'
import { getUserRoleText } from '../badges/status'

const CardWrapper = ({
  children,
  animate = true,
  onClick = () => {},
}: {
  children: React.ReactNode
  animate?: boolean
  onClick?: () => void
}) => {
  return (
    <Card
      className={cn(
        'cursor-pointer hover:bg-muted/50 rounded-[12px]',
        animate && ' transition-colors group',
      )}
      onClick={onClick}
    >
      {children}
    </Card>
  )
}

const CardIconWrapper = ({ icon, className }: { icon: string; className?: string }) => {
  return (
    <CardIcon
      icon={icon}
      className={cn(
        'transition-all duration-300 ease-in-out group-hover:scale-110 group-hover:-translate-y-1 ',
        className,
      )}
    />
  )
}

const StatsWrapper = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return <div className={cn('grid gap-4 grid-cols-2 lg:grid-cols-4', className)}>{children}</div>
}

const AvailableBalanceCard = ({
  availableBalance,
  loading,
  onClick,
}: {
  availableBalance: string
  loading?: boolean
  onClick: () => void
}) => {
  return (
    <CardWrapper onClick={onClick}>
      <CardContent>
        <div className="flex flex-col md:flex-row items-center justify-between gap-2">
          <div className="flex flex-col items-center md:items-start gap-2 order-2 md:order-1">
            <CardValue>{loading ? 'Loading...' : availableBalance}</CardValue>
            <CardTitle className="text-sm font-medium text-card-text">Available Funds</CardTitle>
          </div>
          <CardIconWrapper icon="companies" className={'order-1 md:order-2'} />
        </div>
      </CardContent>
    </CardWrapper>
  )
}

const PendingApprovalsCard = ({
  pendingApprovals,
  onClick,
}: {
  pendingApprovals: number
  onClick: () => void
}) => {
  return (
    <CardWrapper onClick={onClick}>
      <CardContent>
        <div className="flex flex-col md:flex-row items-center justify-between gap-2">
          <div className="flex flex-col items-center md:items-start gap-2 order-2 md:order-1">
            <CardValue>{pendingApprovals ?? '-'}</CardValue>
            <CardTitle className="text-sm font-medium text-card-text">Pending Approvals</CardTitle>
          </div>
          <CardIconWrapper icon="pending-swaps" className={'order-1 md:order-2'} />
        </div>
      </CardContent>
    </CardWrapper>
  )
}

const PendingTransactionsCard = ({
  pendingTransactions,
  onClick,
}: {
  pendingTransactions: number
  onClick: () => void
}) => {
  return (
    <CardWrapper onClick={onClick}>
      <CardContent>
        <div className="flex flex-col md:flex-row items-center justify-between gap-2">
          <div className="flex flex-col items-center md:items-start gap-2 order-2 md:order-1">
            <CardValue>{pendingTransactions ?? '-'}</CardValue>
            <CardTitle className="text-sm font-medium text-card-text">Awaiting Approval</CardTitle>
          </div>
          <CardIconWrapper icon="groups" className={'order-1 md:order-2'} />
        </div>
      </CardContent>
    </CardWrapper>
  )
}

const FlaggedTransactionsCard = ({
  flaggedTransactions,
  onClick,
}: {
  flaggedTransactions: number
  onClick: () => void
}) => {
  return (
    <CardWrapper onClick={onClick}>
      <CardContent>
        <div className="flex flex-col md:flex-row items-center justify-between gap-2">
          <div className="flex flex-col items-center md:items-start gap-2 order-2 md:order-1">
            <CardValue>{flaggedTransactions ?? '-'}</CardValue>
            <CardTitle className="text-sm font-medium text-card-text">
              Flagged Transactions
            </CardTitle>
          </div>
          <CardIconWrapper icon="flags" className={'order-1 md:order-2'} />
        </div>
      </CardContent>
    </CardWrapper>
  )
}

const CurrentMonthlyVolumesCard = ({
  currentMonthlyVolumes,
  previousMonthlyVolumes,
  onClick,
}: {
  currentMonthlyVolumes: string
  previousMonthlyVolumes: string
  onClick: () => void
}) => {
  return (
    <CardWrapper onClick={onClick} animate={false}>
      <CardContent>
        <div className="flex flex-col md:flex-row items-center justify-between gap-2">
          <div className="flex flex-col gap-2 items-center md:items-start">
            <CardValue>{currentMonthlyVolumes}</CardValue>
            <CardTitle className="text-sm font-medium text-card-text text-center md:text-left">
              Current Monthly Volumes
            </CardTitle>
          </div>
          <p className="text-xs bg-foreground/5 rounded-sm px-3 py-2.5 transition-all duration-300 ease-in-out group-hover:scale-110 group-hover:-translate-y-1">
            <span className="block font-medium text-sm text-black">{previousMonthlyVolumes}</span>
            <span className="block font-medium text-card-text">Prev. Monthly Volumes</span>
          </p>
        </div>
      </CardContent>
    </CardWrapper>
  )
}

const LiquidityProviderCard = ({
  liquidityProvider,
  bps,
  valiantBps,
  onClick,
}: {
  liquidityProvider: string
  bps: number
  valiantBps?: number | null
  onClick: () => void
}) => {
  const showValiantBreakdown = valiantBps != null && valiantBps > 0

  return (
    <CardWrapper onClick={onClick} animate={false}>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-2">
            <CardValue>{liquidityProvider}</CardValue>
            <CardTitle className="text-sm font-medium text-card-text">Liquidity Provider</CardTitle>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0 ml-2">
            <p className="flex gap-1 items-center text-xs text-muted-foreground rounded-sm border border-card-border px-2 transition-all duration-300 ease-in-out group-hover:scale-110 group-hover:-translate-y-1">
              <span className="block font-medium text-sm text-card-text">{bps ?? '-'}</span>
              <span className="block font-medium text-sm text-card-text leading-none">BPS</span>
            </p>
            {showValiantBreakdown && (
              <div className="text-right text-xs text-muted-foreground max-w-[10rem] space-y-0.5">
                <div>
                  LP: <span className="font-medium text-card-text">{bps - valiantBps} BPS</span>
                </div>
                <div>
                  Valiant: <span className="font-medium text-card-text">{valiantBps} BPS</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </CardWrapper>
  )
}

const GroupsCard = ({ groups, onClick }: { groups: number; onClick: () => void }) => {
  return (
    <CardWrapper onClick={onClick}>
      <CardContent>
        <div className="flex flex-col md:flex-row items-center justify-between gap-2">
          <div className="flex flex-col items-center md:items-start gap-2 order-2 md:order-1">
            <CardValue>{groups}</CardValue>
            <CardTitle className="text-sm font-medium text-card-text">Active Groups</CardTitle>
          </div>

          <CardIconWrapper icon="groups" className={'order-1 md:order-2'} />
        </div>
      </CardContent>
    </CardWrapper>
  )
}

const CompanyDetailsCard = ({ companyStats }: { companyStats: CompanyStats | null }) => {
  return (
    <CardWrapper animate={false}>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="border-none">
              <TableCell></TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow className="border-none">
              <TableCell className="text-md text-card-text font-medium">Registration #:</TableCell>
              <TableCell className="font-bold">{companyStats?.registrationNumber || '-'}</TableCell>
            </TableRow>
            <TableRow className="border-none">
              <TableCell className="text-md text-card-text font-medium">Director:</TableCell>
              <TableCell className="font-bold">{companyStats?.director || '-'}</TableCell>
            </TableRow>
            <TableRow className="border-none">
              <TableCell className="text-md text-card-text font-medium">Country:</TableCell>
              <TableCell className="font-bold">{companyStats?.country || '-'}</TableCell>
            </TableRow>
            <TableRow className="border-none">
              <TableCell className="text-md text-card-text font-medium ">Address:</TableCell>
              <TableCell className="whitespace-normal font-bold">
                {companyStats?.address || '-'}
              </TableCell>
            </TableRow>
            <TableRow className="border-none">
              <TableCell className="text-md text-card-text font-medium">Website:</TableCell>
              <TableCell className="font-bold flex items-center gap-2">
                <a
                  href={companyStats?.website || '-'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="leading-none"
                >
                  {companyStats?.website || '-'}
                </a>
                {companyStats?.website && <ExternalLink className="w-4 h-4" />}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </CardWrapper>
  )
}

const CompanyBalanceCard = ({
  companyBalance,
  totalUsdValue,
}: {
  companyBalance: { currency: string; amount: string; usdAmount: string; image: string }[]
  /** Pre-formatted API total (includes `$`); already dust-floored under $0.50 for admin/valiant. */
  totalUsdValue: string
}) => {
  const filteredCompanyBalance = companyBalance.filter((holding) => parseFloat(holding.amount) > 0)

  return (
    <CardWrapper onClick={() => {}}>
      <CardContent>
        <div className="text-sm font-medium text-card-text">Balances</div>
        {filteredCompanyBalance && filteredCompanyBalance.length ? (
          filteredCompanyBalance.map((holding) => (
            <div key={holding.currency}>
              <div className="flex items-center justify-between gap-2 py-2">
                <div className="flex items-center gap-2">
                  <CryptoIcon symbol={holding.currency} imageOverride={holding.image} />
                  <span className="text-sm text-card-text">{holding.currency}:</span>
                </div>
                <div className="text-xl font-bold">
                  <span>{holding.amount}</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="flex items-center justify-between">
            <CardValue>No Balances</CardValue>
          </div>
        )}
        <div className="text-sm text-card-text font-medium">Total USD Value: {totalUsdValue}</div>
      </CardContent>
    </CardWrapper>
  )
}

const UserCard = ({
  email,
  role,
  isWhitelisted,
}: {
  email: string
  role: string
  isWhitelisted?: boolean
}) => {
  return (
    <Card>
      <CardContent className="space-y-2">
        <div className="md:text-lg font-medium">
          Email: <span className="font-bold">{email}</span>
        </div>
        <div className="md:text-lg font-medium">
          Role: <span className="font-bold">{getUserRoleText(role)}</span>
        </div>
        {isWhitelisted !== undefined && (
          <div className="md:text-lg font-medium">
            Authorized?: <span className="font-bold">{isWhitelisted ? 'Yes' : 'No'}</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export {
  AvailableBalanceCard,
  FlaggedTransactionsCard,
  CurrentMonthlyVolumesCard,
  LiquidityProviderCard,
  GroupsCard,
  CompanyDetailsCard,
  CompanyBalanceCard,
  PendingApprovalsCard,
  PendingTransactionsCard,
  UserCard,
  StatsWrapper,
}
